import React, { useEffect } from 'react';
import { Modal } from 'antd';
import IconFont from '$/components/IconFont';
import { useLanguageStore } from '$/store/languageStore';
import style from './index.module.scss';

export type LeaveTeamModalVariant = 'confirm' | 'blocked';

interface LeaveTeamModalProps {
  variant: LeaveTeamModalVariant | null;
  isPending: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onGoToMemberManagement: () => void;
}

/**
 * 根据成员身份与团队商业状态展示退出确认或管理员阻断说明。
 */
export default function LeaveTeamModal({
  variant,
  isPending,
  onClose,
  onConfirm,
  onGoToMemberManagement,
}: LeaveTeamModalProps) {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const open = variant !== null;
  const isBlocked = variant === 'blocked';

  // 子弹窗打开时优先消费 Esc，避免同时关闭底层团队管理页。
  useEffect(() => {
    if (!open || typeof document === 'undefined') {
      return undefined;
    }

    /** 请求期间仅拦截关闭，其余场景通过统一回调收起退出弹窗。 */
    const handleEscapeKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (!isPending) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleEscapeKeyDown, true);
    };
  }, [isPending, onClose, open]);

  return (
    <Modal
      open={open}
      centered
      footer={null}
      closable={false}
      width={600}
      zIndex={6300}
      maskClosable={false}
      keyboard={false}
      onCancel={onClose}
      className={style.leaveTeamModal}
    >
      <div
        className={style.modalRoot}
        role="dialog"
        aria-modal="true"
        aria-labelledby="leave-team-title"
        aria-describedby="leave-team-description"
        aria-busy={isPending}
      >
        <div className={style.header}>
          <h2 id="leave-team-title">
            {isBlocked
              ? languageStore.smartcatTranslationText(
                (v) => v.temporarily_unable_to_leave_the_team_,
              )
              : languageStore.smartcatTranslationText((v) => v.leave_the_team)}
          </h2>
          <button
            type="button"
            className={style.closeButton}
            aria-label="关闭退出团队弹窗"
            disabled={isPending}
            onClick={onClose}
          >
            <IconFont name="crossBlack" fontSize={20} color="currentColor" />
          </button>
        </div>

        {isBlocked ? (
          <div id="leave-team-description" className={style.blockedContent}>
            <div className={style.guidanceCard}>
              <IconFont
                className={style.infoIcon}
                name="infoCircleBold"
                fontSize={14}
                color="currentColor"
              />
              <p>
                {languageStore.smartcatTranslationText(
                  (v) => v.go_to_member_management_and_set_a_valid_member_as_an_administrator_after_completion_you_may_proceed_according_to_the_regular_member_exit_procedure_,
                )}
              </p>
            </div>
            <div className={style.insetContent}>
              <h3>
                {languageStore.smartcatTranslationText(
                  (v) => v.team_benefits_will_not_be_transferred_to_individual_accounts_,
                )}
              </h3>
              <div className={style.bulletRow}>
                <span aria-hidden>•</span>
                <p>
                  {languageStore.smartcatTranslationText(
                    (v) => v.remaining_points_assets_and_orders_always_belong_to_the_current_oid_,
                  )}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div id="leave-team-description" className={style.insetContent}>
            <h3>
              {languageStore.smartcatTranslationText(
                (v) => v.exiting_will_trigger_a_change_in_permissions_,
              )}
            </h3>
            <div className={style.bulletRow}>
              <span aria-hidden>•</span>
              <p>
                {languageStore.smartcatTranslationText(
                  (v) => v.access_to_all_shared_resources_models_files_workflows_in_the_team_space_will_be_immediately_revoked_,
                )}
              </p>
            </div>
            <div className={style.bulletRow}>
              <span aria-hidden>•</span>
              <div>
                <p>
                  {languageStore.smartcatTranslationText(
                    (v) => v.historical_collaboration_data_is_still_retained_in_the_team_asset_library_but_viewing_and_editing_are_no_longer_supported_,
                  )}
                </p>
                <p>
                  {languageStore.smartcatTranslationText(
                    (v) => v.it_is_recommended_to_back_up_necessary_data_in_advance_please_note_that_this_operation_is_irreversible_once_confirmed_,
                  )}
                </p>
              </div>
            </div>
            <div className={style.bulletRow}>
              <span aria-hidden>•</span>
              <p>
                {languageStore.smartcatTranslationText(
                  (v) => v.the_content_you_created_in_the_team_is_still_preserved_in_the_original_team_and_continues_to_be_managed_by_the_administrator_,
                )}
              </p>
            </div>
            <div className={style.bulletRow}>
              <span aria-hidden>•</span>
              <p>
                {languageStore.smartcatTranslationText(
                  (v) => v.the_team_points_you_purchased_but_did_not_use_remain_in_the_team_sharing_pool_,
                )}
              </p>
            </div>
          </div>
        )}

        <div className={style.footer}>
          <button
            type="button"
            className={style.cancelButton}
            disabled={isPending}
            onClick={onClose}
          >
            {languageStore.smartcatTranslationText((v) => v.cancel)}
          </button>
          <button
            type="button"
            className={style.confirmButton}
            disabled={isPending}
            onClick={isBlocked ? onGoToMemberManagement : onConfirm}
          >
            {isBlocked
              ? languageStore.smartcatTranslationText((v) => v.go_to_member_management)
              : languageStore.smartcatTranslationText((v) => v.sure)}
          </button>
        </div>
      </div>
    </Modal>
  );
}
